/**
 * Tracks the player's "explorer" progression on Map Nerd: the set of unique
 * country codes ever correctly located, across every scope (world + continents)
 * and both modes (Classic / Hard).
 *
 * Stored under a single raw, language-agnostic localStorage key (a discovery is
 * the same fact whether you played in FR or EN), so it is written directly
 * rather than through StorageService (which would language-prefix it).
 */
import AccountSync from "../services/AccountSync.js";

export default class MapExploration {
    static KEY = "geonerd.mapexploration.discovered";

    /**
     * Rank thresholds on the world discovery percentage (highest first).
     * The label for each key lives in i18n under `mapExploration.ranks.*`.
     */
    static RANKS = [
        { key: "master", min: 100 },
        { key: "globetrotter", min: 70 },
        { key: "adventurer", min: 45 },
        { key: "explorer", min: 20 },
        { key: "tourist", min: 0 }
    ];

    /** @returns {Set<string>} the set of discovered country codes. */
    static getDiscovered() {
        try {
            const raw = localStorage.getItem(MapExploration.KEY);
            if (!raw) return new Set();
            const arr = JSON.parse(raw);
            return new Set(Array.isArray(arr) ? arr : []);
        } catch (e) {
            return new Set();
        }
    }

    /** Mark a country code as discovered (idempotent). */
    static record(code) {
        if (!code) return;
        const set = MapExploration.getDiscovered();
        if (set.has(code)) return;
        set.add(code);
        localStorage.setItem(MapExploration.KEY, JSON.stringify([...set]));
        AccountSync.scheduleSync();
    }

    static reset() {
        localStorage.removeItem(MapExploration.KEY);
    }

    /**
     * Discovery stats over a list of country codes.
     * @param {string[]} codes Codes forming the denominator (a scope's pool).
     * @returns {{found:number, total:number, percent:number}}
     */
    static stats(codes) {
        const set = MapExploration.getDiscovered();
        const total = codes.length;
        const found = codes.reduce((n, code) => n + (set.has(code) ? 1 : 0), 0);
        const percent = total > 0 ? Math.round((found / total) * 100) : 0;
        return { found, total, percent };
    }

    /**
     * Rank key for a given world-discovery percentage.
     * @param {number} percent 0–100
     * @returns {string} one of master/globetrotter/adventurer/explorer/tourist
     */
    static rankFor(percent) {
        return MapExploration.RANKS.find(rank => percent >= rank.min).key;
    }
}
